import { useEffect, useRef, useState } from "react";
import { WebPubSubClient, WebPubSubJsonProtocol } from "@azure/web-pubsub-client";

/** What a negotiate call has to produce: where to connect, and what to join. */
export type Negotiation = {
  url: string;
  /** joined once connected, and rejoined by the SDK after a reconnect */
  group?: string;
};

type Params = {
  /**
   * Resolves a client access url. Called before *every* connect attempt, the
   * reconnects included, so this is where a fresh token belongs; throwing fails that
   * one attempt and nothing else.
   *
   * Must be referentially stable (useCallback) — it is the dependency that owns the
   * connection, so a new identity per render reconnects on every render.
   */
  negotiate: () => Promise<Negotiation>;
  /** false means no client at all; flipping it connects and disconnects */
  enabled: boolean;
  /**
   * Every payload the connection carries, group-addressed or not. Read through a ref
   * on each message, so unlike `negotiate` its identity may change freely.
   */
  onMessage: (payload: any) => void;
};

/**
 * A Web PubSub connection that survives the things that drop it: the access token
 * expiring, a laptop sleeping, the wifi blinking. Reconnect, re-negotiation and
 * group rejoin all belong to @azure/web-pubsub-client; what is left here is binding
 * its lifetime to the effect and its events to React state.
 *
 * Knows nothing about what travels over the socket — see useHeadsetRealtime for the
 * headset side of it.
 *
 * @returns whether the connection is currently up
 */
export function useWebPubSubClient({ negotiate, enabled, onMessage }: Params): boolean {
  const [connected, setConnected] = useState(false);

  // the latest handler without making it a dependency of the connection
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let retryTimer: number | null = null;
    let attempt = 0;
    /**
     * Learned from negotiate, used by the "connected" handler below. Written here
     * rather than returned because the SDK wants a bare url back, and read from
     * there because that is the first moment it can be acted on; the ordering holds
     * because the SDK negotiates before it opens the socket, so this is always set
     * by the time anything connects.
     */
    let group: string | null = null;

    async function getClientAccessUrl(): Promise<string> {
      const negotiated = await negotiate();
      group = negotiated.group ?? null;
      return negotiated.url;
    }

    const client = new WebPubSubClient(
      { getClientAccessUrl },
      {
        // the plain json subprotocol, which is what the headset side speaks; the SDK
        // would otherwise default to json.reliable
        protocol: WebPubSubJsonProtocol(),
        /**
         * Without a retry the dock, which is hidden while the socket is down,
         * stayed hidden until the page was reloaded.
         *
         * autoReconnect retries indefinitely on its own; the options below only
         * restore the 1s-doubling-to-30s backoff over the SDK default of a flat 1s.
         */
        autoReconnect: true,
        reconnectRetryOptions: {
          mode: "Exponential",
          retryDelayInMs: 1000,
          maxRetryDelayInMs: 30_000,
        },
        // put us back in the group after a reconnect without us asking
        autoRejoinGroups: true,
      },
    );

    client.on("connected", () => {
      // the connection held, so the next failed start begins its backoff at 1s again
      attempt = 0;
      setConnected(true);
      // already done for us once autoRejoinGroups has handled a reconnect; this is
      // the first join, and harmless afterwards
      if (group) client.joinGroup(group).catch(() => undefined);
    });

    client.on("disconnected", () => setConnected(false));

    // only reached once the reconnect loop itself gives up, which its effectively
    // unlimited retry budget makes rare — but not never
    client.on("stopped", () => {
      setConnected(false);
      scheduleStart();
    });

    client.on("group-message", (e) => onMessageRef.current(e.message.data));
    // the same payloads, addressed to this connection rather than to the group
    client.on("server-message", (e) => onMessageRef.current(e.message.data));

    /**
     * The SDK retries reconnects but not the *initial* start: a negotiate refused
     * while the dock is opening simply rejects, so that first attempt needs a
     * backoff of its own.
     */
    function scheduleStart() {
      if (cancelled || retryTimer !== null) return;
      const delay = Math.min(30_000, 1000 * 2 ** attempt);
      attempt += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        void start();
      }, delay);
    }

    async function start() {
      if (cancelled) return;
      try {
        await client.start();
      } catch {
        // negotiate refused or the socket could not be constructed — try again
        scheduleStart();
      }
    }

    void start();

    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      // fires "stopped", which calls scheduleStart — `cancelled` is already true by
      // then, so it schedules nothing
      try {
        client.stop();
      } catch {
        // ignore
      }
      setConnected(false);
    };
  }, [enabled, negotiate]);

  return connected;
}
